import React, { useEffect, useRef, useState } from 'react';
import { cn } from './cn';

// shadcn's Chart anatomy (ChartContainer / ChartTooltipContent / ChartLegend)
// for hand-drawn SVG charts: Recharts is not a dependency, so the container,
// tooltip card and legend reproduce its markup and tokens instead.

// Tracks a container's rendered width so an SVG viewBox can match it 1:1 —
// stretching a fixed viewBox would warp text and markers.
function useContainerWidth(initial) {
  const ref = useRef(null);
  const [width, setWidth] = useState(initial);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver !== 'function') return undefined;
    const measure = () => {
      const next = el.clientWidth;
      if (next) setWidth((prev) => (Math.round(next) !== Math.round(prev) ? next : prev));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

const ChartContainer = React.forwardRef(function ChartContainer({ className, ...props }, ref) {
  return <div ref={ref} className={cn('ui-chart', className)} {...props} />;
});

function ChartIndicator({ color, shape = 'square' }) {
  return (
    <span
      aria-hidden
      className={cn('ui-chart-indicator', shape !== 'square' && `ui-chart-indicator--${shape}`)}
      style={{ '--indicator': color }}
    />
  );
}

// Positioned against the ChartContainer. `x`/`y` are the anchor point in the
// container's pixel space; the card flips sides so it never leaves the box.
function ChartTooltipContent({ x, y, containerWidth, label, items = [], footer }) {
  const width = 176;
  const flipLeft = x + 14 + width > containerWidth;
  const style = {
    top: Math.max(0, y - 12),
    left: flipLeft ? undefined : x + 14,
    right: flipLeft ? Math.max(0, containerWidth - x + 14) : undefined,
  };
  return (
    <div className="ui-chart-tooltip" style={style}>
      {label && <div className="ui-chart-tooltip__label">{label}</div>}
      {items.map((item) => (
        <div key={item.name} className="ui-chart-tooltip__row">
          {item.color && <ChartIndicator color={item.color} shape={item.shape} />}
          <span className="ui-chart-tooltip__name">{item.name}</span>
          <span className="ui-chart-tooltip__value">{item.value}</span>
        </div>
      ))}
      {footer && <div className="ui-chart-tooltip__name">{footer}</div>}
    </div>
  );
}

function ChartLegend({ className, ...props }) {
  return <div className={cn('ui-chart-legend', className)} {...props} />;
}

function ChartLegendItem({ color, shape, strong, children }) {
  return (
    <span className={cn('ui-chart-legend__item', strong && 'ui-chart-legend__item--strong')}>
      <ChartIndicator color={color} shape={shape} />
      {children}
    </span>
  );
}

export {
  ChartContainer, ChartIndicator, ChartTooltipContent, ChartLegend, ChartLegendItem, useContainerWidth,
};
